import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {    
    const { dialog } = this.refs;
    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    // Prevent layout thrashing by separating DOM reads from DOM writes
    // For search-modal, do NOT use requestAnimationFrame (for iPhone keyboard); for others, use it
    const openDialog = () => {
      document.body.classList.add('modal-open');
      document.body.style.top = `-${scrollY}px`;
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      // FOCUS IMMEDIATELY after showModal, not in setTimeout
      const inputEle = document.getElementById('autocomplete-0-input');
      if (inputEle) {
        inputEle.focus({ preventScroll: true });
        try { inputEle.setSelectionRange(inputEle.value.length, inputEle.value.length); } catch (e) {}
        if (inputEle.form) {
          inputEle.form.classList.add('highlight-input');
          if (!inputEle.hasAttribute('tabindex') || inputEle.getAttribute('tabindex') !== '0') {
            inputEle.setAttribute('tabindex', '0');
          }
          inputEle.addEventListener('focus', function() {
            inputEle.form.classList.add('highlight-input');
          });
          inputEle.addEventListener('blur', function() {
            inputEle.form.classList.remove('highlight-input');
          });
        }
      }
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    //OS-6833 requestAnimationFrame need to be removed for iphone to directly open the input keyboard
    if (dialog.className.includes('search-modal')) {
      // Open immediately for search-modal (no rAF)
      openDialog();
    } else {
      // Use rAF for all other dialogs
      requestAnimationFrame(openDialog);
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    dialog.scrollTop = 0;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.classList.remove('modal-open');
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    // Some dialogs (e.g. cart drawer) should not auto-close on backdrop/outside click.
    if (this.hasAttribute('disable-outside-close')) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
       document.body.classList.remove('modal-open');
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    // Some dialogs (e.g. cart drawer) should not auto-close on Escape.
    if (this.hasAttribute('disable-escape-close')) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
